import type { LngLat, PlaceMatch } from '@chukua/common';
import { AutoComplete, Button, Form, Input } from 'antd';
import { MapPinOff, Search } from 'lucide-react';
import type { RefObject } from 'react';
import { SEARCH_INPUT_PROPS } from '../../../shared/policies/search-input-props';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { placeSearchEmptyMessage, placeSearchOptions, placeSearchPick } from '../../zones/public/place-search-options';
import { usePlaceSearch } from '../../zones/public/use-place-search';
import { useZonesMapConfig } from '../../zones/public/use-zones-map-config';
import { useLocationPicker } from '../hooks/use-location-picker';

interface CompanyLocationFieldProps {
  /** From the Form.Item it sits in. */
  value?: LngLat | null;
  onChange?: (location: LngLat | null) => void;
}

function describe(location: LngLat): string {
  return `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`;
}

/**
 * Where the company is (its head office or main yard), shown on the Zones map: found by name, then fine-tuned
 * by clicking the map or dragging the pin. A picked place also fills in the address while that is still empty.
 */
export function CompanyLocationField({ value = null, onChange }: CompanyLocationFieldProps) {
  const form = Form.useFormInstance();
  const mapConfig = useZonesMapConfig();
  const tileKey = mapConfig?.tileKey ?? null;
  const picker = useLocationPicker({ tileKey, value, onChange: (location) => onChange?.(location) });
  const search = usePlaceSearch(null, { includeZones: false });

  function pickPlace(place: PlaceMatch) {
    const location = { lng: place.center[0], lat: place.center[1] };
    onChange?.(location);
    picker.showLocation(location);
    if (!form.getFieldValue('address')) form.setFieldsValue({ address: [place.name, place.context].filter(Boolean).join(', ') });
  }

  function select(option: string) {
    const pick = placeSearchPick(option, search.state);
    search.setText('');
    if (pick?.type === 'place') pickPlace(pick.place);
  }

  return (
    <div className="flex flex-col gap-2">
      <AutoComplete
        className="w-full"
        value={search.text}
        options={placeSearchOptions(search.state)}
        onSearch={search.setText}
        onSelect={select}
        notFoundContent={placeSearchEmptyMessage(search.state)}
      >
        <Input
          {...SEARCH_INPUT_PROPS}
          aria-label="Find the company's location"
          prefix={<Search className="size-4 text-muted" />}
          placeholder="Office, yard or town"
        />
      </AutoComplete>
      <LocationMap tileKey={tileKey} isLoading={mapConfig === undefined} containerRef={picker.containerRef} />
      <div className="flex items-center justify-between gap-3 text-xs text-muted">
        <span>
          {value
            ? `Pinned at ${describe(value)}. Click the map or drag the pin to move it.`
            : 'Head office or main yard: the Zones map marks it and opens on it. Find it by name, or click the map.'}
        </span>
        {value && (
          <Button size="small" type="link" className="px-0" onClick={() => onChange?.(null)}>
            Clear location
          </Button>
        )}
      </div>
    </div>
  );
}

interface LocationMapProps {
  tileKey: string | null;
  isLoading: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
}

function LocationMap({ tileKey, isLoading, containerRef }: LocationMapProps) {
  if (isLoading) {
    return (
      <SkeletonRegion label="Loading map">
        <Skeleton className="h-56 rounded-lg" />
      </SkeletonRegion>
    );
  }
  if (!tileKey) {
    return (
      <p className="flex items-center gap-2 rounded-lg border border-dashed border-line bg-page px-3 py-4 text-sm text-muted">
        <MapPinOff className="size-4 shrink-0" />
        No map on this deployment (no MapTiler key). Find the location by name instead.
      </p>
    );
  }
  return <div ref={containerRef} className="h-56 w-full rounded-lg border border-line" />;
}
